import axios from 'axios';
import { API_URL } from '../config';

// Configuração base do axios para Campanhas (disparo manual de WhatsApp)
const campaignApi = axios.create({
  baseURL: `${API_URL}/api/campaigns`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token de autenticação
campaignApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratamento de respostas
campaignApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expirado ou inválido
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Interfaces
export interface ManualDispatchScheduleEntry {
  hora: string; // "HH:MM", fuso America/Sao_Paulo
  quantidade: number;
}

export interface Campaign {
  id: number;
  name: string;
  status: 'draft' | 'approved' | 'active' | 'paused' | 'completed' | string;
  total_leads: number;
  messages_sent: number;
  conversions: number;
  created_at: string;
  manual_dispatch_schedule?: ManualDispatchScheduleEntry[] | null;
}

export interface CampaignCreateData {
  name: string;
  description?: string;
  message_template?: string;
  whatsapp_template_id?: string;
  whatsapp_template_language?: string;
  max_messages_per_day?: number;
  messages_per_minute?: number;
  manual_dispatch_schedule?: ManualDispatchScheduleEntry[];
}

export interface ValidationError {
  row: number;
  error: string;
  data?: Record<string, any>;
}

export interface LeadUploadResponse {
  id: number;
  campaign_id: number;
  audience_id: number;
  status: string;
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
  duplicates: number;
  validation_errors: ValidationError[];
  leads_created: number[];
  processed_at: string;
}

export interface ManualSendPendingItem {
  lead_id: number;
  nome: string;
  telefone: string;
  whatsapp_link: string;
  message_preview: string;
}

export interface ManualSendPendingResponse {
  campaign_id: number;
  total_pending: number;
  items: ManualSendPendingItem[];
  available_now?: number | null;
}

export interface ManualSendMarkResponse {
  success: boolean;
  log_id: number;
  lead_id: number;
  remaining_pending?: number;
  already_sent?: boolean;
}

export interface ManualSendLostResponse {
  success: boolean;
  lead_id: number;
  remaining_pending: number;
}

// Funções do serviço

export const listCampaigns = async (): Promise<Campaign[]> => {
  const response = await campaignApi.get('/');
  return response.data;
};

export const createCampaign = async (data: CampaignCreateData): Promise<Campaign> => {
  const response = await campaignApi.post('/', data);
  return response.data;
};

export const approveCampaign = async (campaignId: number): Promise<Campaign> => {
  const response = await campaignApi.post(`/${campaignId}/approve`);
  return response.data;
};

export const startCampaign = async (campaignId: number): Promise<Campaign> => {
  const response = await campaignApi.post(`/${campaignId}/start`);
  return response.data;
};

export const uploadLeadsFile = async (
  campaignId: number,
  file: File,
  audienceName: string,
  columnMapping?: Record<string, string>
): Promise<LeadUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('audience_name', audienceName);
  formData.append('column_mapping', JSON.stringify(columnMapping || {}));

  const response = await campaignApi.post(`/${campaignId}/leads/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const attachExistingLeads = async (
  campaignId: number,
  audienceName?: string,
  status: string = 'novo'
): Promise<LeadUploadResponse> => {
  const response = await campaignApi.post(`/${campaignId}/leads/attach-existing`, {
    audience_name: audienceName || undefined,
    status,
  });
  return response.data;
};

export const getPendingManualSend = async (
  campaignId: number,
  skip: number = 0,
  limit: number = 50
): Promise<ManualSendPendingResponse> => {
  const response = await campaignApi.get(`/${campaignId}/manual-send/pending`, {
    params: { skip, limit },
  });
  return response.data;
};

export const markLeadAsSent = async (
  campaignId: number,
  leadId: number
): Promise<ManualSendMarkResponse> => {
  const response = await campaignApi.post(`/${campaignId}/manual-send/mark-sent`, {
    lead_id: leadId,
  });
  return response.data;
};

export const markLeadAsLost = async (
  campaignId: number,
  leadId: number
): Promise<ManualSendLostResponse> => {
  const response = await campaignApi.post(`/${campaignId}/manual-send/mark-lost`, {
    lead_id: leadId,
  });
  return response.data;
};

const campaignManualService = {
  listCampaigns,
  createCampaign,
  approveCampaign,
  startCampaign,
  uploadLeadsFile,
  attachExistingLeads,
  getPendingManualSend,
  markLeadAsSent,
  markLeadAsLost,
};

export default campaignManualService;
